import React, { useEffect } from 'react';
import { CalendarIcon, Ticket, AlertCircle, Download, Clock, FileDown, Eye, Search, Coins, X } from 'lucide-react';
import axios from "../../axios-config";
import { toast } from 'react-toastify';
import { generateCouponPDF } from "./CouponPDF";
import { useOrganizations } from "../../contexts/OrganizationsContext";
import { useBadges } from "../../contexts/BadgeContext";

type BadgeAward = {
  badgeId: string;
  xpAmount: number;
};

type CouponFormData = {
  prefix: string;
  description: string;
  pointValue: number;
  quantity: number;
  expirationDate?: string;
  organizationId?: string;
  badgeXps: BadgeAward[];
};

interface CouponGeneratorProps {
  activeTab: 'generate' | 'history' | 'redemptions';
}

export default function CouponGenerator({ activeTab }: CouponGeneratorProps) {
  const { organizations } = useOrganizations();
  const { badges } = useBadges();
  const [formData, setFormData] = React.useState<CouponFormData>({
    prefix: '',
    description: '',
    pointValue: 1,
    quantity: 1,
    organizationId: organizations?.[0]?.organizationId || "",
    badgeXps: []
  });
  const [error, setError] = React.useState<string>('');
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<
    'all' | 'issued' | 'reissued' | 'redeemed' | 'lost' | 'stolen'
  >('all');
  const [redemptions, setRedemptions] = React.useState<any[]>([]);
  const [couponHistory, setCouponHistory] = React.useState<any[]>([]);
  const [grouped, setGrouped] = React.useState(true);

  useEffect(() => {
    const fetchAcorns = async () => {
      try {
        const { data } = await axios.get('/api/acorns', { withCredentials: true });
        setCouponHistory(data);
      } catch (err) {
        console.error('Error fetching acorns:', err);
      }
    };
    fetchAcorns();

    const fetchRedemptions = async () => {
      try {
        // Updated endpoint returns new redemption structure
        const { data } = await axios.get('/api/Acorns/history', { withCredentials: true });
        setRedemptions(data);
      } catch (err) {
        console.error('Error fetching redemptions:', err);
      }
    };
    fetchRedemptions();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate prefix
    if (formData.prefix.length !== 3) {
      setError('Prefix must be exactly 3 characters');
      return;
    }

    // Validate point value
    if (formData.pointValue < 1) {
      setError('Point value must be at least 1');
      return;
    }

    // Validate quantity
    if (formData.quantity < 1 || formData.quantity > 1000) {
      setError('Quantity must be between 1 and 1000');
      return;
    }

    for (const award of formData.badgeXps) {
      if (!award.badgeId) continue;
      if (award.xpAmount <= 0) {
        setError('XP amount must be greater than 0');
        return;
      }
    }

    setIsGenerating(true);
    try {
      const { data } = await axios.post("/api/Acorns/Generate", {
        OrganizationId: formData.organizationId,
        prefix: formData.prefix,
        points: formData.pointValue,
        numAcorns: formData.quantity,
        description: formData.description,
        expiryDate: formData.expirationDate,
        badgeXps: formData.badgeXps.filter(b => b.badgeId)
      }, { withCredentials: true });
      
      setCouponHistory(prev => [...data, ...prev]);
      await generateCouponPDF(data);
      toast.success("Coupons generated successfully!");
      
      // Reset form after successful generation
      setFormData({
        prefix: '',
        description: '',
        pointValue: 1,
        quantity: 1,
        organizationId: organizations?.[0]?.organizationId || "",
        badgeXps: []
      });
    } catch (err) {
      setError('Failed to generate coupons');
      toast.error("Failed to generate coupons");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleBadgeChange = (index: number, field: keyof BadgeAward, value: any) => {
    setFormData(prev => {
      const badgeXps = prev.badgeXps.map((b, i) => i === index ? { ...b, [field]: value } : b);
      return { ...prev, badgeXps };
    });
  };

  const addBadgeRow = () => {
    setFormData(prev => ({ ...prev, badgeXps: [...prev.badgeXps, { badgeId: '', xpAmount: 1 }] }));
  };

  const removeBadgeRow = (index: number) => {
    setFormData(prev => ({ ...prev, badgeXps: prev.badgeXps.filter((_, i) => i !== index) }));
  };

  const handleDownloadBatch = async (acornsOrAcorn: any) => {
    setError("");
    try {
      const acornsArray = Array.isArray(acornsOrAcorn) ? acornsOrAcorn : [acornsOrAcorn];
      await generateCouponPDF(acornsArray, { isGrouped: acornsArray.length > 1 });
      toast.success("PDF generated successfully!");
    } catch (err) {
      setError("Failed to download coupon");
      toast.error("Failed to generate PDF");
    }
  };

  const groupedData = React.useMemo(() => {
    if (!grouped) return [];
    const prefixMap: Record<string, any[]> = {};
    couponHistory.forEach(acorn => {
      const prefix = acorn.code?.slice(0, 3) ?? "";
      if (!prefixMap[prefix]) prefixMap[prefix] = [];
      prefixMap[prefix].push(acorn);
    });
    return Object.entries(prefixMap).map(([prefix, coupons]) => ({ prefix, coupons }));
  }, [grouped, couponHistory]);

  // Updated handleViewDetails function to support single coupon or an array of coupons
  const handleViewDetails = async (acornOrCoupons: any) => {
    try {
      const coupons = Array.isArray(acornOrCoupons) ? acornOrCoupons : [acornOrCoupons];
      const pdfBlob: Blob = (await generateCouponPDF(coupons, { returnBlob: true })) as Blob;
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, "_blank", "noopener,noreferrer");
      toast.info("Coupon details opened in a new tab.");
    } catch (err) {
      console.error("Failed to view coupon details", err);
      toast.error("Failed to view coupon details");
    }
  };

  return (
        <div className="bg-white/60 backdrop-blur-md rounded-2xl shadow-sm border border-gray-200/50 p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
            <Ticket className="w-6 h-6 text-indigo-500" />
            <h1 className="text-2xl font-bold text-gray-800">Generate Reward Coupons</h1>
            </div>
          </div>

          {activeTab === 'generate' ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Prefix Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prefix (3 characters)
              </label>
              <input
                type="text"
                value={formData.prefix}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase().slice(0, 3);
                  setFormData(prev => ({ ...prev, prefix: value }));
                }}
                className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                placeholder="ABC"
                pattern="[A-Z]{3}"
                required
              />
            </div>

            {/* Description Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (optional)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                placeholder={`Enter coupon description or leave empty for a random wisdom quote`}
                rows={3}
              />
            </div>

            {/* Point Value Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Point Value
              </label>
              <input
                type="number"
                value={formData.pointValue}
                onChange={(e) => setFormData(prev => ({ ...prev, pointValue: parseInt(e.target.value) || 0 }))}
                className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                min="1"
                required
              />
            </div>

            {/* Quantity Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Number of Coupons
              </label>
              <input
                type="number"
                value={formData.quantity}
                onChange={(e) => setFormData(prev => ({ ...prev, quantity: parseInt(e.target.value) || 0 }))}
                className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                min="1"
                max="1000"
                required
              />
            </div>

            {/* Expiration Date Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expiration Date (optional)
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={formData.expirationDate || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, expirationDate: e.target.value }))}
                  className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                  min={new Date().toISOString().split('T')[0]}
                />
                <CalendarIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Organization Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Organization
              </label>
              <select
                value={formData.organizationId}
                onChange={(e) => setFormData(prev => ({ ...prev, organizationId: e.target.value }))}
                className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                required
              >
                {organizations?.map(org => (
                  <option key={org.organizationId} value={org.organizationId}>
                    {org.organizationName}
                  </option>
                ))}
              </select>
            </div>

            {/* Badge Rewards */}
            <div className="space-y-4">
              {formData.badgeXps.map((award, index) => (
                <div key={index} className="relative border rounded-lg p-4 space-y-4 bg-white/50">
                  <button type="button" className="absolute top-1 right-1 text-gray-400 hover:text-red-600" onClick={() => removeBadgeRow(index)}>
                    <X className="w-4 h-4" />
                  </button>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Badge</label>
                    <select
                      value={award.badgeId}
                      onChange={(e) => handleBadgeChange(index, 'badgeId', e.target.value)}
                      className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                    >
                      <option value="">Select a badge...</option>
                      {badges.map(b => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">XP Amount</label>
                    <input
                      type="number"
                      min="1"
                      value={award.xpAmount}
                      onChange={(e) => handleBadgeChange(index, 'xpAmount', Number(e.target.value))}
                      className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                    />
                  </div>
                </div>
              ))}
              <button type="button" onClick={addBadgeRow} className="text-indigo-600 hover:underline text-sm">Add Badge</button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 text-red-600 p-4 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            {/* Preview */}
            <div className="bg-gray-50/50 rounded-lg p-4 border border-gray-200/50">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Preview</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p>Prefix: <span className="font-mono">{formData.prefix || 'ABC'}</span></p>
                <p>Description: {formData.description}</p>
                <p>Point Value: {formData.pointValue} points</p>
                <p>Quantity: {formData.quantity} coupon{formData.quantity !== 1 ? 's' : ''}</p>
                {formData.expirationDate && (
                  <p>Expires: {new Date(formData.expirationDate).toLocaleDateString()}</p>
                )}
                {formData.badgeXps.filter(b => b.badgeId).length > 0 && (
                  <div>
                    <p className="font-medium">Badge Rewards:</p>
                    <ul className="list-disc ml-4">
                      {formData.badgeXps.filter(b => b.badgeId).map((b, idx) => {
                        const badge = badges.find(bd => bd.id === b.badgeId);
                        return (
                          <li key={idx}>{badge?.name || 'Badge'} - {b.xpAmount} XP</li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={isGenerating}
                className="flex-1 bg-indigo-500 text-white px-4 py-2 rounded-lg hover:bg-indigo-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm font-medium flex items-center justify-center gap-2"
              >
                {isGenerating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Ticket className="w-4 h-4" />
                    Generate Coupons
                  </>
                )}
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>
          </form>
          ) : activeTab === 'history' ? (
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="grouped-checkbox"
                  className="cursor-pointer"
                  checked={grouped}
                  onChange={() => setGrouped(!grouped)}
                />
                <label htmlFor="grouped-checkbox" className="text-sm text-gray-700">
                  Grouped
                </label>
              </div>
              {grouped ? (
                groupedData.map(group => (
                  <div key={group.prefix} className="bg-white/50 rounded-lg border border-gray-200/50 p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium text-gray-800">
                          Prefix: {group.prefix}
                        </h3>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDownloadBatch(group.coupons)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600"
                        >
                          <FileDown className="w-5 h-5" />
                        </button>
                        <button
                          // Updated to pass all coupons for preview when grouped is checked
                          onClick={() => handleViewDetails(group.coupons)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600"
                          title="View Details"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                couponHistory.map(acorn => (
                  <div key={acorn.acornId} className="bg-white/50 rounded-lg border border-gray-200/50 p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium text-gray-800">{acorn.code}</h3>
                        <p className="text-sm text-gray-500">Points: {acorn.points}</p>
                        <p className="text-sm text-gray-600 mb-2">{acorn.acornDescription}</p>
                        {acorn.acornHistories?.[0]?.createdAt && (
                          <div className="flex items-center gap-1 text-sm text-gray-500">
                            <Clock className="w-4 h-4" />
                            {new Date(acorn.acornHistories[0].createdAt).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDownloadBatch(acorn)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600"
                          title="Download PDF"
                        >
                          <FileDown className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleViewDetails(acorn)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600"
                          title="View Details"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : ( // redemptions tab
            <div className="space-y-6">
              {/* Search and Filters */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search by code or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 bg-white/50 border border-gray-200 rounded-lg w-full focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                  className="px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                >
                  <option value="all">All Statuses</option>
                  <option value="issued">Issued</option>
                  <option value="reissued">Re‑issued</option>
                  <option value="redeemed">Redeemed</option>
                  <option value="lost">Lost</option>
                  <option value="stolen">Stolen</option>
                </select>
                <button className="px-4 py-2 bg-white/50 text-gray-700 rounded-lg hover:bg-white transition-colors flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Export Report
                </button>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white/50 rounded-lg p-4 border border-gray-200/50">
                  <div className="flex items-center gap-2 mb-1">
                    <Ticket className="w-4 h-4 text-green-500" />
                    <span className="text-sm font-medium text-gray-600">Total Redeemed</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-800">{redemptions.length}</p>
                </div>
                <div className="bg-white/50 rounded-lg p-4 border border-gray-200/50">
                  <div className="flex items-center gap-2 mb-1">
                    <Coins className="w-4 h-4 text-blue-500" />
                    <span className="text-sm font-medium text-gray-600">Points Redeemed</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-800">
                    {redemptions.reduce((sum, r) => sum + Number(r.points), 0)}
                  </p>
                </div>
                <div className="bg-white/50 rounded-lg p-4 border border-gray-200/50">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-purple-500" />
                    <span className="text-sm font-medium text-gray-600">Last 24 Hours</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-800">
                    {redemptions.filter(r => {
                      const hours = (Date.now() - new Date(r.createdAt).getTime()) / (1000 * 60 * 60);
                      return hours <= 24;
                    }).length}
                  </p>
                </div>
              </div>

              {/* Redemptions List */}
              <div className="space-y-4">
                {redemptions
                  .filter(redemption => {
                    /* ---------- search filter ---------- */
                    const search = searchTerm.toLowerCase();
                    const code        = redemption.acorn?.code?.toLowerCase() ?? '';
                    const description = redemption.acorn?.acornDescription?.toLowerCase() ?? '';
                    const matchesSearch = code.includes(search) || description.includes(search);
                
                    /* ---------- status filter ---------- */
                    if (statusFilter === 'all') return matchesSearch;
                
                    /*  If your API returns a status *id* (1‑5) instead of a name,
                        keep the map below; if it returns the text (‘Issued’, …)
                        you can compare the lower‑cased text directly.               */
                    const statusIdMap = {
                      issued: 1,
                      reissued: 2,
                      redeemed: 3,
                      lost: 4,
                      stolen: 5,
                    };
                
                    const statusId   = redemption.acornStatusId ?? redemption.statusId;     // adjust to your model
                    const statusName = redemption.acornStatus?.statusName?.toLowerCase();    // if you get the text
                
                    const matchesStatus =
                      statusId === statusIdMap[statusFilter] ||
                      statusName === statusFilter;
                
                    return matchesSearch && matchesStatus;
                  })
                  .map(redemption => {
                    return (
                      <div
                        key={redemption.acornDetailId}
                        className="bg-white/50 rounded-lg border border-gray-200/50 p-4"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-medium text-gray-800">
                                {redemption.acorn.code}
                              </h3>
                              <span className="text-sm text-gray-500">
                                ({redemption.points} points)
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">
                              {redemption.acorn.acornDescription}
                            </p>
                            <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                              <div className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                Redeemed {new Date(redemption.createdAt).toLocaleString()}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
  );
}